<template>
  <div :style="{ width: '100%' }">
    <div :style="{ width: '100%' }" @click="toggleOverlayPanel">
      <!-- Input of the created offer product in the table -->
      <TableListItem
        v-if="tableData"
        :is-pay-later="isPayLater"
        :offer-product="tableData"
        :selected-price-type="selectedPriceType"
      />

      <!-- Input of the offer product in the creation form -->
      <CreateRewardsFormInput
        v-else
        :selected-price-type="selectedPriceType"
        :is-saved="isSaved"
        :form-state="formState"
        :disabled="disabled"
      />
    </div>

    <!-- Popover -->
    <Popover ref="overlayPanel">
      <div :class="$style.overlayContainer" :style="{ gap: selectedPriceType === 'FREE' ? '0' : '8px' }">
        <div :class="$style.dropdownContainer">
          <Select
            v-model="selectedPriceType"
            :options="priceTypes"
            option-label="name"
            option-value="value"
            :style="{ minWidth: '220px' }"
            @update:model-value="handlePriceTypeChange"
          >
            <template #value="slotProps">
              <VdIcon color="var(--vd-color-text-light-2)" store="fas" :name="getIconName(slotProps.value)"></VdIcon>
              <span :class="$style.label">{{ getSelectedName(slotProps.value) }}</span>
            </template>

            <template #option="slotProps">
              <VdIcon color="var(--vd-color-text-light-2)" :name="getIconName(slotProps.option.value)"></VdIcon>
              <span :class="$style.label">{{ slotProps.option.name }}</span>
            </template>
          </Select>

          <Select
            v-if="selectedPriceType === 'IN_GAME_CURRENCY'"
            v-model="formState.selectedInGamePrice"
            :options="dropdownItems"
            option-label="name"
            placeholder="Select an item"
            append-to="self"
            :style="{ height: '40px' }"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value" :class="$style.itemOption">
                <img
                  v-if="slotProps.value.sprite"
                  :alt="slotProps.value.name"
                  :src="slotProps.value.sprite.imageUrl"
                  :class="$style.sprite"
                />
                <div>{{ slotProps.value.name }}</div>
              </div>
              <span v-else>
                {{ slotProps.placeholder }}
              </span>
            </template>
            <template #option="slotProps">
              <div :class="$style.itemOption">
                <img
                  v-if="slotProps.option.sprite"
                  :alt="slotProps.option.name"
                  :src="slotProps.option.sprite.imageUrl"
                  :class="$style.sprite"
                />
                <div>{{ slotProps.option.name }}</div>
              </div>
            </template>
          </Select>
        </div>
        <div v-if="nonConsumableIds.length > 0" :class="$style.infoMessage">
          <VdIcon store="fas" name="circle-info" color="black" />
          <span>A non-consumable is selected, so only IAPs associated with this non-consumable will be available</span>
        </div>
        <div>
          <div v-if="selectedPriceType === 'IAP'">
            <div>
              <div :class="$style.formRow">
                <IAPSelectDropdown
                  :model-value="formState.iosIapId"
                  :platform="Platform.Ios"
                  :game-id="gameId"
                  :version-id="versionId"
                  :non-consumable-ids="nonConsumableIds"
                  @update="(event) => handleIapIdChange(event, Platform.Ios)"
                />
                <IAPSelectDropdown
                  :model-value="formState.androidIapId"
                  :platform="Platform.Android"
                  :game-id="gameId"
                  :version-id="versionId"
                  :non-consumable-ids="nonConsumableIds"
                  @update="(event) => handleIapIdChange(event, Platform.Android)"
                />
              </div>
              <Button label="Save" :disabled="!formState.iosIapId || !formState.androidIapId" @click="saveIapId" />
            </div>
          </div>
          <div v-if="selectedPriceType === 'RV'">
            <div :class="$style.formRow">
              <div :class="$style.formColumn">
                <span>iOS</span>
                <InputNumber v-model="formState.selectedIosRVAmount" :min="1" :style="{ minWidth: '220px' }" />
              </div>
              <div :class="$style.formColumn">
                <span>Android</span>
                <InputNumber v-model="formState.selectedAndroidRVAmount" :min="1" :style="{ minWidth: '220px' }" />
              </div>
            </div>
            <Button label="Save" @click="handleRVAmountChange" />
          </div>
          <div v-if="selectedPriceType === 'IN_GAME_CURRENCY'">
            <div :class="$style.formRow">
              <div :class="$style.formColumn">
                <span>iOS</span>
                <InputNumber v-model="formState.selectedIosInGamePriceAmount" :min="1" :style="{ minWidth: '220px' }" />
              </div>
              <div :class="$style.formColumn">
                <span>Android</span>
                <InputNumber
                  v-model="formState.selectedAndroidInGamePriceAmount"
                  :min="1"
                  :style="{ minWidth: '220px' }"
                />
              </div>
            </div>
            <Button label="Save" :disabled="!formState.selectedInGamePrice" @click="handleInGamePriceChange" />
          </div>
        </div>
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import IAPSelectDropdown from "@/components/IAPSelectDropdown";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import {
  IapType,
  OfferBuyAllDetails,
  OfferProduct,
  Platform,
  StoreItem,
  StoreItemTypeEnum,
  useGetInAppPurchasesQuery,
  useGetItemsNameAndSpriteQuery,
} from "@/graphql";
import Button from "primevue/button";
import InputNumber from "primevue/inputnumber";
import Popover from "primevue/popover";
import Select from "primevue/select";
import { PropType, computed, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import CreateRewardsFormInput from "./CreateRewardsFormInput.vue";
import TableListItem from "./TableListItem.vue";

const emit = defineEmits([
  "update-iap-id",
  "update-price-type",
  "update-rv-amount",
  "update-in-game-price",
  "update-free-reward",
]);

const props = defineProps({
  isEditable: {
    type: Boolean,
    default: true,
  },
  tableData: {
    type: Object as PropType<OfferProduct>,
  },
  selectedPriceType: {
    type: String,
    required: true,
    default: "IAP",
  },
  isPayLater: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  nonConsumableIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  buyAllPrice: {
    type: Object as PropType<OfferBuyAllDetails | null>,
    default: null,
  },
  isBuyAllEnabled: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const gameId = route.params.gameId as string;
const versionId = route.params.versionId as string;

const { result } = useGetItemsNameAndSpriteQuery({ gameId, versionId });
const dropdownItems = computed(
  () => result.value?.getStoreItems.filter((item) => item?.type === StoreItemTypeEnum.Currency) || []
);

const { refetch: fetchAndroidIaps } = useGetInAppPurchasesQuery(
  {
    gameId,
    versionId,
    countryIso: "USA",
    platform: Platform.Android,
    type: [IapType.NonConsumable, IapType.Consumable],
    onlyOriginalIaPs: false,
  },
  {
    enabled: !!gameId,
    fetchPolicy: "network-only",
  }
);

const { refetch: fetchIosIaps } = useGetInAppPurchasesQuery(
  {
    gameId,
    versionId,
    countryIso: "USA",
    platform: Platform.Ios,
    type: [IapType.NonConsumable, IapType.Consumable],
    onlyOriginalIaPs: false,
  },
  {
    enabled: !!gameId,
    fetchPolicy: "network-only",
  }
);

const getIapAmount = async (iapId: string, platform: Platform) => {
  let response;
  if (platform === Platform.Android) {
    response = await fetchAndroidIaps();
  } else {
    response = await fetchIosIaps();
  }

  const selectedIAp = response?.data?.getInAppPurchases?.find((iap) => iap?.id === iapId);
  const usaPricing = selectedIAp?.pricing?.find((price) => price?.countryIso === "USA");
  return `${usaPricing?.value} ${usaPricing?.currency}`;
};

const overlayPanel = ref<InstanceType<typeof Popover> | null>(null);

const selectedPriceType = ref(props.selectedPriceType);

watch(
  () => props.selectedPriceType,
  (value) => {
    selectedPriceType.value = value;
  }
);

const isSaved = ref(false);

const priceTypes = computed(() => [
  { name: "Free", value: "FREE" },
  { name: "RV", value: "RV" },
  { name: "In game currency", value: "IN_GAME_CURRENCY" },
  { name: "Payment", value: "IAP" },
]);

const formState = reactive({
  iosIapAmount: "",
  androidIapAmount: "",
  iosIapId: props.tableData?.iosPrice.IAPId || "",
  androidIapId: props.tableData?.androidPrice.IAPId || "",
  selectedInGamePrice: props.tableData?.iosPrice.inGamePrice?.item || null,
  selectedIosInGamePriceAmount: props.tableData?.iosPrice.inGamePrice?.quantity || 1,
  selectedAndroidInGamePriceAmount: props.tableData?.androidPrice.inGamePrice?.quantity || 1,
  selectedIosRVAmount: props.tableData?.iosPrice.rvQuantity || 1,
  selectedAndroidRVAmount: props.tableData?.androidPrice.rvQuantity || 1,
});

const getIconName = (name: string) => {
  switch (name) {
    case "IAP":
      return "money-bill";
    case "IN_GAME_CURRENCY":
    case "FREE":
      return "box";
    case "RV":
      return "clapperboard-play";
    default:
      return "";
  }
};

const getSelectedName = (value: string) => {
  const selected = priceTypes.value.find((type) => type.value === value);
  return selected ? selected.name : "";
};

const handleFreeRewardChange = () => {
  isSaved.value = true;
  emit("update-free-reward");
  overlayPanel.value?.hide();
};

const handlePriceTypeChange = (value: string) => {
  isSaved.value = false;
  selectedPriceType.value = value;

  formState.iosIapAmount = "";
  formState.androidIapAmount = "";
  formState.iosIapId = "";
  formState.androidIapId = "";
  formState.selectedInGamePrice = null;
  formState.selectedIosInGamePriceAmount = 1;
  formState.selectedAndroidInGamePriceAmount = 1;
  formState.selectedIosRVAmount = 1;
  formState.selectedAndroidRVAmount = 1;

  if (value === "FREE") {
    handleFreeRewardChange();
  }

  emit("update-price-type", value);
};

interface Event {
  id: string;
  price: string;
}

const handleIapIdChange = (event: Event, platform: Platform) => {
  if (platform === Platform.Ios) {
    formState.iosIapAmount = event.price;
    formState.iosIapId = event.id;
  } else {
    formState.androidIapAmount = event.price;
    formState.androidIapId = event.id;
  }
};

const saveIapId = () => {
  isSaved.value = true;

  emit("update-iap-id", { ios: formState.iosIapId, android: formState.androidIapId });

  overlayPanel.value?.hide();
};

const handleRVAmountChange = () => {
  isSaved.value = true;

  emit("update-rv-amount", {
    ios: formState.selectedIosRVAmount || 1,
    android: formState.selectedAndroidRVAmount || 1,
  });

  overlayPanel.value?.hide();
};

const handleInGamePriceChange = () => {
  isSaved.value = true;

  emit("update-in-game-price", {
    ios: {
      itemId: formState.selectedInGamePrice?.id || "",
      quantity: formState.selectedIosInGamePriceAmount || 1,
    },
    android: {
      itemId: formState.selectedInGamePrice?.id || "",
      quantity: formState.selectedAndroidInGamePriceAmount || 1,
    },
  });

  overlayPanel.value?.hide();
};

const toggleOverlayPanel = (event: MouseEvent) => {
  if (!props.isEditable || props.disabled) {
    return;
  }

  overlayPanel.value?.toggle(event);
};

watch(
  () => props.nonConsumableIds,
  (value) => {
    if (value.length > 0) {
      handlePriceTypeChange("IAP");
    }
  }
);

watch(
  () => props.buyAllPrice,
  async () => {
    if (props.buyAllPrice !== null) {
      formState.iosIapId = props.buyAllPrice.iosIAPId || "";
      formState.androidIapAmount = props.buyAllPrice.androidIAPId
        ? await getIapAmount(props.buyAllPrice.androidIAPId, Platform.Android)
        : "";
      formState.iosIapAmount = props.buyAllPrice.iosIAPId
        ? await getIapAmount(props.buyAllPrice.iosIAPId, Platform.Ios)
        : "";
      formState.androidIapId = props.buyAllPrice.androidIAPId || "";
      formState.selectedInGamePrice =
        (dropdownItems.value.find((item) => item?.id === props.buyAllPrice?.iosInGameCurrencyId) as StoreItem) || null;
      formState.selectedIosInGamePriceAmount = props.buyAllPrice.iosInGameCurrencyQuantity || 1;
      formState.selectedAndroidInGamePriceAmount = props.buyAllPrice.androidInGameCurrencyQuantity || 1;
      formState.selectedIosRVAmount = props.buyAllPrice.iosRVQuantity || 1;
      formState.selectedAndroidRVAmount = props.buyAllPrice.androidRVQuantity || 1;
      isSaved.value = true;
    }
  },
  { deep: true, immediate: true }
);

watch(
  () => props.isBuyAllEnabled,
  (value) => {
    if (!value) {
      isSaved.value = false;
    }
  }
);
</script>

<style module lang="scss">
.overlayContainer {
  padding: 4px;
  display: flex;
  flex-direction: column;
}

.dropdownContainer {
  display: flex;
  width: 50%;
  align-items: center;
  gap: 8px;
}

.label {
  color: var(--vd-color-text-dark);
  margin-left: 8px;
}

.sprite {
  width: 16px;
  margin-right: 0.5rem;
}

.itemOption {
  display: flex;
}

.formRow {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  margin-top: 0.5rem;
}

.formColumn {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.infoMessage {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  margin-top: 0.5rem;
  border-radius: 0.5rem;
  background-color: var(--vd-color-blue-light-9);
  font-size: 12px;

  svg {
    margin-right: 0.75rem;
  }
}

.inputDisabled {
  background-color: var(--vd-color-surface-200);
}
</style>
